import { MessageBarType } from '@fluentui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { BwjViewState, BwjViewManager } from '../../../../framework/components/bwj-view-manager';
import { xlationContext, xlate } from '../../../../framework/utils/i18n';
import { PresentationApiClient } from '../../../../services/presentation';
import { PresentationEditor } from '../../components/presentation-editor';
import * as formService from '../../form-services/presentation-editor';
import { useParams } from 'react-router-dom';
import { useStandardResourceNotFoundState } from '../../../../entities/view-manager-standard/resource-not-found';
import { useStandardErrorState } from '../../../../entities/view-manager-standard/error';
import { useNavigator } from '../../../../framework/services/navigation';
import { AppRoutes } from '../../../app-routes';

xlationContext('Update presentation page');

const subscriberId = 'Page.UpdatePresentationPage';

export const UpdatePresentationPage: React.FunctionComponent = () => {
  const { id: presentationId } = useParams<{id: string}>();
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const [savingPresentation, setSavingPresentation] = useState(false);
  const formModel = formService.useFormModel(subscriberId);
  const errorState = useStandardErrorState();
  const notFoundState = useStandardResourceNotFoundState();
  const { showToast } = useToast();
  const navigator = useNavigator();

  useEffect(() => {
    PresentationApiClient.getPresentationForEditing(presentationId)
    .then(dataModel => {
      formService.initializeService(dataModel);
      setViewState(BwjViewState.Ready);
    })
    .catch((statusCode) => {
      if(statusCode === 404) {
        setViewState(BwjViewState.PageNotFound);
      } else {
        setViewState(BwjViewState.Error);
      }
    });
  }, [presentationId]);

  const onSavePresentation = useCallback(() => {
    const request = formService.toUpdateRequest();
    PresentationApiClient.updatePresentation(request)
    .then(() => {
      formService.markAllChangesSaved();
      showToast(MessageBarType.success, xlate('Discussion plan saved successfully'));
    })
    .catch(() => {
      showToast(MessageBarType.error, xlate('An error occurred during save - please try again'), true);
    })
    .finally(() => setSavingPresentation(false));
  }, [showToast]);

  // triggers presentation save after button is disabled
  useEffect(() => {
    if(savingPresentation) {
      onSavePresentation();
    }
  }, [savingPresentation, onSavePresentation]);

  const onBeginDiscussion = () => {
    navigator.goTo(AppRoutes.performPresentation.url(presentationId));
  };

  return (
    <BwjViewManager
      state={viewState}
      loadingState={{ message: xlate('One moment please...') }}
      pageNotFoundState={notFoundState}
      errorState={errorState}
    >
      <PresentationEditor
        formModel={formModel}
        onSavePresentation={() => setSavingPresentation(true)}
        onStartPresentation={onBeginDiscussion}
        savingPresentation={savingPresentation}
      />
    </BwjViewManager>
  );
};
